// Pure graph mutation helpers

/**
 * Validate a node id format. Conservative: letters/underscore start, then alphanumerics/underscore, max 128.
 */
export function validateNodeIdFormat(id) {
  const s = String(id || '').trim();
  if (!s) return { ok: false, error: 'empty id' };
  // Disallow reserved sentinels
  if (s.toUpperCase() === 'START' || s.toUpperCase() === 'END') return { ok: false, error: 'reserved id (START/END) not allowed' };
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(s)) return { ok: false, error: 'invalid format (use [A-Za-z_][A-Za-z0-9_]*)' };
  if (s.length > 128) return { ok: false, error: 'too long (max 128)' };
  return { ok: true };
}

/**
 * Rename node ids and rewire edges.
 * - mapping: { oldId: newId, ... }
 * - Validates new id format and uniqueness (against existing nodes and within mapping targets)
 * - Updates node.id, node.data.id, node.data.question_id
 * - Rewrites edges where source/target == oldId
 * Returns { nodes, edges }
 */
export function renameNodeIds({ nodes = [], edges = [] }, mapping = {}) {
  const mapEntries = Object.entries(mapping || {}).filter(([oldId, newId]) => String(oldId) !== String(newId));
  if (mapEntries.length === 0) return { nodes, edges };

  const existingIds = new Set(nodes.map(n => String(n.id)));
  const targetIds = new Set(mapEntries.map(([, newId]) => String(newId)));
  if (targetIds.size !== mapEntries.length) {
    throw new Error('Duplicate targets in id mapping.');
  }
  // Validate formats and uniqueness
  for (const [oldId, newId] of mapEntries) {
    const fmt = validateNodeIdFormat(newId);
    if (!fmt.ok) throw new Error(`New id '${newId}' invalid: ${fmt.error}`);
    const newIdStr = String(newId);
    const oldIdStr = String(oldId);
    // allow collision with the same node being renamed; forbid others
    if (existingIds.has(newIdStr) && newIdStr !== oldIdStr) {
      // Is the node with newIdStr also being renamed away? If not, collision
      const takenByOther = !mapEntries.find(([o]) => String(o) === newIdStr);
      if (takenByOther) throw new Error(`New id '${newIdStr}' collides with existing node.`);
    }
  }

  // Build quick map
  const mapObj = new Map(mapEntries.map(([a, b]) => [String(a), String(b)]));

  // Rewrite nodes
  const newNodes = nodes.map(n => {
    const idStr = String(n.id);
    if (!mapObj.has(idStr)) return n;
    const newId = mapObj.get(idStr);
    const newData = { ...(n.data || {}) };
    if (Object.prototype.hasOwnProperty.call(newData, 'id')) newData.id = newId;
    if (Object.prototype.hasOwnProperty.call(newData, 'question_id')) newData.question_id = newId;
    return { ...n, id: newId, data: newData };
  });

  // Rewrite edges
  const newEdges = edges.map(e => {
    const src = String(e.source);
    const tgt = String(e.target);
    const nextSrc = mapObj.has(src) ? mapObj.get(src) : e.source;
    const nextTgt = mapObj.has(tgt) ? mapObj.get(tgt) : e.target;
    if (nextSrc === e.source && nextTgt === e.target) return e;
    return { ...e, source: nextSrc, target: nextTgt };
  });

  return { nodes: newNodes, edges: newEdges };
}


